// ============================================================
// TMR Portal Admin — formatação, máscaras e validação (puro)
// ============================================================
import { maskCEP, onlyDigits, toE164Phone } from '@tmr/br-forms';

// The document helpers moved to @tmr/br-forms (shared with the public site, 28 §9); re-exported so the
// portal keeps importing them from here.
export {
  isEmail,
  isValidCNPJ,
  isValidCPF,
  maskCEP,
  maskCNPJ,
  maskCPF,
  maskDigits,
  onlyDigits,
} from '@tmr/br-forms';

/**
 * Builds a `wa.me` deep link for a phone number, assuming Brazil (+55) when the
 * number comes in national format. Returns `null` when the digit count matches
 * no known shape, so callers can simply skip rendering the link.
 *
 * This is a convenience shortcut, not a guarantee: the number is never checked
 * against WhatsApp, so a link may open on an account that does not exist.
 */
export function waHref(phone?: string): string | null {
  const canonical = toE164Phone(phone ?? '');
  return canonical ? `https://wa.me/${onlyDigits(canonical)}` : null;
}

export function maskSubdomain(v: string): string {
  return (v || '').toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 16);
}

/** Gera CPFs mock com dígito verificador válido a partir de uma base de 9 dígitos. */
function calcDV(nums: number[]): number {
  let sum = 0;
  let w = nums.length + 1;
  for (const n of nums) sum += n * w--;
  const r = sum % 11;
  return r < 2 ? 0 : 11 - r;
}

export function makeCPF(base9: string): string {
  const d = base9.split('').map(Number);
  const dv1 = calcDV(d);
  const dv2 = calcDV([...d, dv1]);
  const full = [...d, dv1, dv2].join('');
  return `${full.slice(0, 3)}.${full.slice(3, 6)}.${full.slice(6, 9)}-${full.slice(9)}`;
}

/**
 * How precisely a company can be placed on a map. Companies migrated from the
 * legacy system often carry only `city`, so `full` is not guaranteed.
 */
export type AddressPrecision = 'full' | 'city' | 'none';

export interface CompanyAddress {
  /** Human-readable address — what is shown on screen and copied to the clipboard. */
  text: string;
  /** Map lookup string. Drops the unit/suite, which only degrades geocoding. */
  query: string;
  precision: AddressPrecision;
}

/**
 * The address fields this helper reads. Structural on purpose: the full record satisfies it, and so does
 * a list item that only carries city and state — which is the common case, because most Empresas came
 * from the legacy with nothing more.
 */
export interface AddressParts {
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  district?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  country?: string | null;
}

/** Assembles an address for display and for a map lookup, degrading to the city when that is all there is. */
export function companyAddress(parts: AddressParts | null | undefined): CompanyAddress {
  const cityState = [parts?.city, parts?.state].filter(Boolean).join('/');

  if (parts?.street) {
    const street = [parts.street, parts.number].filter(Boolean).join(', ');
    const streetWithUnit = parts.complement ? `${street} – ${parts.complement}` : street;
    const zipCode = parts.zipCode ? `CEP ${maskCEP(parts.zipCode)}` : '';
    return {
      text: [streetWithUnit, parts.district, cityState, zipCode].filter(Boolean).join(' · '),
      query: [street, parts.district, cityState, parts.zipCode, parts.country || 'Brasil']
        .filter(Boolean)
        .join(', '),
      precision: 'full',
    };
  }
  if (cityState) {
    return { text: cityState, query: `${cityState}, Brasil`, precision: 'city' };
  }
  return { text: '', query: '', precision: 'none' };
}

/** Embedded map for an address. Requires a Maps Embed API key restricted by HTTP referrer. */
export function mapsEmbedUrl(query: string, key: string): string {
  return `https://www.google.com/maps/embed/v1/place?key=${encodeURIComponent(key)}&q=${encodeURIComponent(query)}&language=pt-BR&region=BR`;
}

/** Google Maps search link — the fallback whenever the embedded map is unavailable. */
export function mapsExternalUrl(query: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

export function fmtInt(n: number): string {
  return Math.round(n).toLocaleString('pt-BR');
}

export function fmtMoney(n: number): string {
  return 'R$ ' + Math.abs(n).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
